// src/oura/components/GatedReason.tsx
//
// Why a scan produced no usable score.
//
// The drill-in used to say "Gated — no usable score. The latest run couldn't
// produce a reliable score for this site." and stop there. That is a dead end:
// a crawl that could not reach the site, a provider throttling us, and a stage
// that crashed are three completely different problems with three different
// responses, and staff could only tell them apart by opening DevTools.
//
// The backend has been sending `error_code`, `error_detail`, `stage` and
// `engine_version` on every failed or partial run since #3618. Nothing here is
// new data — it is data that was already arriving and being thrown away.
//
// A PARTIAL RUN IS NOT A FAILED ONE, and this used to read as though it were.
// A scan that asked 49 of 50 questions, scored the business and showed Done on
// the Onboarding step still printed "The run stopped with QUERY_SET_NARROWED."
// over an amber warning triangle, with "Stopped at: finalize" under it (#4025
// FE-1). Nothing stopped: the backend narrowed the question set on purpose,
// because the business had no evidence for some angles and the alternative was
// to invent questions and claim the same 50 every other business was measured
// on. So a partial run is a NOTICE about what was measured, and only a failed
// one is a warning about what was not.
//
// THE CODE IS THE FIRST DEGRADATION, NOT THE ONLY ONE. The backend picks one
// code from an ordered `if`/`elif` chain (`aeo_worker.py:4458-4487`), and
// `degraded_reason` — QUERY_SET_NARROWED and GEMINI_QUOTA — is tested BEFORE
// `below_floor`. So a run that narrowed its question set AND sampled an engine
// below the safety floor reports QUERY_SET_NARROWED, and SAMPLE_BELOW_FLOOR is
// never surfaced at all. Nothing here may therefore promise that the rest of
// the run was healthy (Jack, #4027): each sentence says only what its own code
// proves. The two codes ranked below `below_floor` — REPORT_RENDER_FAILED and
// AUDIT_VISIBILITY_ONLY — can only appear when the floor was met, so those two
// may speak for the measurement.
//
// AND NO RAW CODE REACHES STAFF. An unmapped code used to be printed as the
// sentence itself, which told an account manager nothing and read as a crash.
// Every code the backend can finalise a partial run with is mapped below; an
// unmapped one gets a plain sentence, and the code goes on the element as
// `data-error-code` so it is still one click away in DevTools for whoever is
// triaging.

import { AlertTriangleIcon, InfoIcon } from "lucide-react";
import { cn } from "../lib/cn";

/**
 * Plain-English cause and next step per backend error code.
 *
 * `detail` from the payload is shown verbatim underneath when present, because
 * it carries the specifics (which URL, which provider, which stage) that a
 * static string cannot. This map only has to explain the CLASS of failure.
 */
interface Reason {
    cause: string;
    next: string;
    /**
     * Opt out of printing `error_detail`. It is shown by default because it
     * carries the specifics a static string cannot — but two codes carry a
     * stack trace in it, and a Playwright timeout pasted under a sentence is
     * the same mistake as printing the code: text written for an engineer,
     * put in front of an account manager.
     */
    hideDetail?: true;
}

const REASON: Record<string, Reason> = {
    SITE_UNREACHABLE: {
        cause: "We couldn't reach the website during the crawl.",
        next: "Check the domain is correct and the site is publicly reachable, then run a new scan.",
    },
    QUERY_GEN_FAILED: {
        cause: "We couldn't generate the customer questions for this business.",
        next: "This usually means the business details were too thin. Re-run the scan with more specific areas and services.",
    },
    GEMINI_THROTTLE_TIMEOUT: {
        cause: "Google's AI engine rate-limited us for long enough that the run timed out.",
        next: "Nothing is wrong with the site. Run the scan again — ideally not at the same time as other scans.",
    },
    STAGE_CRASH: {
        cause: "A step in the assessment pipeline crashed.",
        next: "This is a platform fault, not a problem with the site. Re-run the scan; if it keeps happening, send this error to engineering.",
    },
    SAMPLE_BELOW_FLOOR: {
        cause: "The engines returned too few answers to score this business honestly.",
        next: "Re-run the scan. A score from this few samples would not be reliable, which is why none is shown.",
    },
    ENGINE_STUBBED: {
        cause: "The AI visibility engine wasn't running for this scan, so no engines were actually asked.",
        next: "This is an environment configuration issue — the scan needs re-running once the engine is enabled.",
    },
    // The engine subprocess codes. These were missing on the first pass and are
    // the ones most likely to appear today, since the engine is the least
    // settled part of the pipeline.
    ENGINE_TIMEOUT: {
        cause: "The scoring engine took longer than its time limit on one stage, so the run was stopped.",
        next: "The site is fine — this is the engine, not the website. Re-run the scan; if it keeps timing out at the same stage, the limit for that stage needs raising.",
    },
    ENGINE_NOT_INSTALLED: {
        cause: "The scoring engine is switched on but isn't present in the deployed image.",
        next: "A deployment problem, not a site problem. Send this to engineering — no amount of re-running will fix it.",
    },
    ENGINE_FAILED: {
        cause: "The scoring engine exited with an error partway through.",
        next: "Re-run the scan. If it fails the same way twice, send this error to engineering.",
    },
    ENGINE_BAD_OUTPUT: {
        cause: "The scoring engine returned output we couldn't read.",
        next: "A platform fault, not a site problem. Re-run, and send this to engineering if it repeats.",
    },
    GOOSE_API_KEY_FAILED: {
        cause: "An AI provider rejected our API key, so the engines couldn't be asked.",
        next: "Nothing to do with this business. Engineering needs to rotate or fix the provider key.",
    },
    MISSING_GOOSE_API_KEY: {
        cause: "An AI provider key is missing from this environment.",
        next: "A configuration gap — engineering needs to add the key. Re-running will not help.",
    },
    SCRAPE_TIMEOUT: {
        cause: "The site didn't respond in time during the crawl.",
        next: "Check the site loads quickly and is publicly reachable, then run a new scan.",
    },
    POLL_TIMEOUT: {
        cause: "The run was still going after the maximum wait, so we stopped tracking it.",
        next: "It may still have finished on the server — reload the page before re-running.",
    },
    POLL_FAILED: {
        cause: "We lost contact with the run while it was in progress.",
        next: "Reload the page to pick it up again; re-run the scan if it doesn't come back.",
    },
    // The four codes a run can finish PARTIAL with. Each sentence is the
    // backend's own meaning, read out of `aeo_worker.py` on staging rather
    // than guessed from the code's name.
    QUERY_SET_NARROWED: {
        // `aeo_worker.py:2214-2222`: an angle with no evidence behind it is
        // measured with fewer questions rather than invented ones, and that is
        // what keeps the run from finalising as `complete` and quietly
        // claiming the same 50 questions as everyone else.
        cause: "This business was measured on fewer questions than the full set.",
        // NOT "nothing needs re-running": this code is chosen before the
        // backend's sample-floor check, so it can hide one. It speaks for the
        // narrowing and nothing else.
        next: "The questions that were asked are the ones this business had evidence for, and narrowing on its own is not a reason to run the scan again.",
    },
    GEMINI_QUOTA: {
        // `aeo_worker.py:2284-2298`: no Gemini slot, so Gemini is dropped and
        // the rest of the engines carry the run.
        cause: "Google's Gemini had no capacity left, so this scan was measured without it.",
        // NOT "the other engines' answers are complete": same ordering, so
        // another engine may also have sampled below the floor without that
        // being the code reported.
        next: "What the other engines answered was measured as usual. Run the scan again later if you need Gemini's share of the picture.",
    },
    REPORT_RENDER_FAILED: {
        // `aeo_worker.py:4472-4482`: the assessment finished; only the PDF did
        // not. Its detail ends in the renderer's exception.
        cause: "The scan finished, but its PDF report could not be produced.",
        next: "Every number on screen is from the finished scan. Re-run the report; if it fails again, send this to engineering.",
        hideDetail: true,
    },
    AUDIT_VISIBILITY_ONLY: {
        // `aeo_worker.py:4483-4487`: visibility-only mode, so the site-health
        // checks are absent rather than zero.
        cause: "Only the AI visibility half of this scan ran, so the site checks are not measured.",
        next: "The AI numbers stand. Site Health and AI Readability have no result from this scan rather than a low one.",
    },
};

/**
 * What a PARTIAL scan left out, as one sentence for under the figures it
 * qualifies: the server's own words when it sent them, else the code's cause.
 * Null for any scan that is not partial, whose notice stays a banner.
 *
 * The merchant's Visibility screen reads it under its tiles, beside the count
 * it explains ("46 of 49"), instead of a banner above the page title.
 */
export function partialScanNote({ errorCode, notice, status }: { errorCode?: string | null; notice?: string | null; status?: string }): string | null {
    if (status !== "partial") return null;
    return (notice ?? "").trim() || (errorCode ? REASON[errorCode]?.cause : undefined) || "This scan finished, but not everything was measured.";
}

export function GatedReason({
    errorCode,
    errorDetail,
    notice,
    stage,
    status,
    inline,
}: {
    errorCode?: string;
    errorDetail?: string;
    /**
     * The server's own sentence for a partial scan (`notice_detail`, BE-3).
     *
     * IT WINS over everything below when it is there. It is written for staff,
     * it is computed from the whole run rather than from the first code that
     * matched, and it is therefore the one thing here that can describe a scan
     * that degraded in two ways at once — which is what no single `error_code`
     * could do, and what made the old copy overstate (Jack, #4027).
     *
     * The map below stays as the fallback, for a payload stored before BE-3
     * and for the `failed` runs it never covers.
     */
    notice?: string | null;
    stage?: string;
    status?: string;
    /**
     * Rendered above a business that DID produce results, rather than in place
     * of them. A partial run still fills the screen with numbers, so without
     * this the fact that the engine never ran is invisible — the scores just
     * look low instead of unmeasured.
     */
    inline?: boolean;
}) {
    const known = errorCode ? REASON[errorCode] : undefined;
    // The server's sentence, only where the server sends one: a partial scan.
    const served = status === "partial" ? (notice ?? "").trim() || null : null;
    // A run that produced results and a run that produced none are different
    // facts and get different chrome. Anything that is not `partial` is
    // treated as the worse case, so an unfamiliar status never reads as fine.
    const partial = status === "partial";
    const tone = partial
        ? { box: "border-blue-200 bg-blue-50", icon: "text-blue-600", head: "text-blue-900", body: "text-blue-800", foot: "text-blue-900" }
        : { box: "border-amber-200 bg-amber-50", icon: "text-amber-600", head: "text-amber-900", body: "text-amber-800", foot: "text-amber-900" };
    const Icon = partial ? InfoIcon : AlertTriangleIcon;

    return (
        <div
            // The code, for whoever is triaging, without putting it on screen.
            data-error-code={errorCode || undefined}
            className={cn("rounded-lg border px-4 py-3 text-left", tone.box, inline ? "mb-4" : "mx-auto max-w-lg")}
        >
            <div className="flex items-start gap-2.5">
                <Icon className={cn("mt-0.5 h-4 w-4 flex-shrink-0", tone.icon)} />
                <div className="min-w-0 space-y-1.5">
                    <p className={cn("text-sm font-medium", tone.head)}>
                        {served
                            ? served
                            : known
                            ? known.cause
                            // Never the code itself. It named a thing staff
                            // cannot act on and read as a crash on a scan that
                            // had in fact finished and scored.
                            : partial
                                ? "This scan finished, but not everything was measured."
                                : status === "failed"
                                    ? "The run failed before it could produce a score."
                                    : "The run finished without producing a score."}
                    </p>

                    {!served && errorDetail && !known?.hideDetail && (
                        <p className={cn("text-xs leading-relaxed", tone.body)}>{errorDetail}</p>
                    )}

                    {/* Which stage it died on is the single most useful field
                        for anyone triaging a FAILED run, and it costs one line.
                        On a partial run nothing died: "Stopped at: finalize"
                        on a finished, scored scan is simply untrue. */}
                    {stage && !partial && (
                        <p className={cn("text-xs", tone.body)}>
                            Stopped at: <span className="font-medium">{stage}</span>
                        </p>
                    )}

                    {!served && known && (
                        <p className={cn("pt-0.5 text-xs leading-relaxed", tone.foot)}>{known.next}</p>
                    )}
                </div>
            </div>
        </div>
    );
}
